/**
 * Imperative app dialogs (alert / confirm) — replaces browser window.alert/confirm.
 * DialogProvider registers the handler; call sites can await without React hooks.
 */

let handler = null;
const queue = [];

export function registerAppDialogHandler(next) {
  handler = next;
  if (handler) {
    while (queue.length) {
      const job = queue.shift();
      handler(job).catch(job.reject);
    }
  }
}

function enqueue(payload) {
  return new Promise((resolve, reject) => {
    const job = { ...payload, resolve, reject };
    if (handler) {
      handler(job).catch(reject);
    } else {
      queue.push(job);
    }
  });
}

/**
 * Show a blocking-style alert. Resolves when the user dismisses.
 * @param {string} message
 * @param {{ title?: string, confirmLabel?: string }} [options]
 * @returns {Promise<void>}
 */
export function appAlert(message, options = {}) {
  const text = message == null ? '' : String(message);
  return enqueue({
    kind: 'alert',
    message: text,
    title: options.title || 'Notice',
    confirmLabel: options.confirmLabel || 'OK',
  });
}

/**
 * Show a confirm dialog. Resolves true if confirmed, false if cancelled.
 * @param {string} message
 * @param {{ title?: string, confirmLabel?: string, cancelLabel?: string, danger?: boolean }} [options]
 * @returns {Promise<boolean>}
 */
export function appConfirm(message, options = {}) {
  const text = message == null ? '' : String(message);
  const looksDestructive =
    options.danger === true ||
    /\b(delete|remove|deactivate|discard|cancel this|overwrite|soft-delete|unassign|reset)\b/i.test(text);
  return enqueue({
    kind: 'confirm',
    message: text,
    title: options.title || (looksDestructive ? 'Please confirm' : 'Confirm'),
    confirmLabel: options.confirmLabel || (looksDestructive ? 'Confirm' : 'OK'),
    cancelLabel: options.cancelLabel || 'Cancel',
    danger: options.danger != null ? options.danger : looksDestructive,
  });
}
